import React, { useEffect, useState, createElement } from 'react';
import { ArrowRightIcon, CheckIcon, PaletteIcon, CodeIcon, SearchIcon, ServerIcon, UserIcon, BuildingIcon, SettingsIcon, ChevronDownIcon, ChevronUpIcon } from 'lucide-react';
import { FinalCtaSection } from '../components/FinalCtaSection';
export function KostenWebsiteLatenMakenPage() {
  const [openFaq, setOpenFaq] = useState<number | null>(0);
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'Wat kost een website laten maken? | Prijzen 2026 | Noord';
    let metaDesc = document.querySelector('meta[name="description"]') as HTMLMetaElement;
    if (!metaDesc) {
      metaDesc = document.createElement('meta');
      metaDesc.name = 'description';
      document.head.appendChild(metaDesc);
    }
    metaDesc.content = 'Wat kost een website laten maken in 2026? Prijzen vanaf €500 voor ZZP tot €8.000+ voor maatwerk. Transparant overzicht van kosten per type website.';
    // Open Graph meta tags
    const ogTags: Record<string, string> = {
      'og:title': 'Wat kost een website laten maken? | Prijzen 2026 | Noord',
      'og:description': 'Wat kost een website laten maken in 2026? Prijzen vanaf €500 voor ZZP tot €8.000+ voor maatwerk.',
      'og:type': 'article',
      'og:url': 'https://noorddigital.nl/kosten-website-laten-maken',
      'og:site_name': 'Noord Digital',
      'og:locale': 'nl_NL'
    };
    Object.entries(ogTags).forEach(([property, content]) => {
      let tag = document.querySelector(`meta[property="${property}"]`) as HTMLMetaElement;
      if (!tag) {
        tag = document.createElement('meta');
        tag.setAttribute('property', property);
        document.head.appendChild(tag);
      }
      tag.content = content;
    });
  }, []);
  const scrollToContact = (e: React.MouseEvent) => {
    e.preventDefault();
    document.getElementById('contact')?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  const toggleFaq = (index: number) => {
    setOpenFaq(openFaq === index ? null : index);
  };
  const faqs = [{
    q: 'Wat kost een website gemiddeld?',
    a: 'De gemiddelde kosten voor een professionele bedrijfswebsite liggen tussen de €1.500 en €3.500. Een eenvoudige starterswebsite kan al vanaf €500, terwijl maatwerk websites met geavanceerde functies €3.000 tot €8.000 of meer kunnen kosten.'
  }, {
    q: 'Hoeveel kost een WordPress website?',
    a: "Een WordPress website kost gemiddeld tussen de €1.000 en €4.000, afhankelijk van het aantal pagina's, het design en de gewenste functionaliteiten. WordPress is een uitstekende keuze vanwege de flexibiliteit en het gebruiksgemak."
  }, {
    q: 'Zijn er maandelijkse kosten?',
    a: 'Ja, naast de eenmalige bouwkosten zijn er maandelijkse kosten voor hosting, onderhoud en eventueel doorlopende SEO. Bij Noord Digital bieden wij transparante onderhoudspakketten zodat je precies weet waar je aan toe bent.'
  }, {
    q: 'Kan een website goedkoper?',
    a: 'Er zijn goedkopere opties zoals doe-het-zelf bouwers (Wix, Squarespace), maar deze bieden minder flexibiliteit, slechtere SEO-mogelijkheden en een minder professionele uitstraling. Een professionele website is een investering die zich terugverdient door meer klanten en aanvragen.'
  }];
  return <main className="pt-16 lg:pt-[72px]" data-id="element-1370" data-mp="2hfblxb5564tb">
      {/* Hero Section */}
      <section className="relative py-20 lg:py-32 bg-navy-900 overflow-hidden" data-id="element-1371" data-mp="2mdmvdkriydb0">
        <div className="absolute inset-0 pointer-events-none" data-id="element-1372" data-mp="2rbodo3h4m8lf">
          <div className="absolute -top-32 -right-32 w-[500px] h-[500px] rounded-full opacity-20 blur-3xl bg-accent" data-id="element-1373" data-mp="2ra3m8mq3z5rs" />
          <div className="absolute bottom-0 left-0 w-[400px] h-[400px] rounded-full opacity-10 blur-3xl bg-accent-light" data-id="element-1374" data-mp="2vbp0ociajs43" />
          <div className="absolute inset-0 opacity-[0.03]" style={{
          backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)',
          backgroundSize: '32px 32px'
        }} data-id="element-1375" data-mp="1100kk70bpji4" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-1376" data-mp="5pklf3vjeqm5">
          {/* Breadcrumb */}
          <nav className="flex items-center justify-center gap-2 text-sm mb-8 animate-fade-in-up" data-id="element-1377" data-mp="kn4kju5r5rke">
            <a href="#/" className="text-navy-400 hover:text-white transition-colors" data-id="element-1378" data-mp="fmym9v4z96tc">
              Home
            </a>
            <span className="text-navy-500" data-id="element-1379" data-mp="3o0ywwwwis23g">/</span>
            <a href="#/kennisbank" className="text-navy-400 hover:text-white transition-colors" data-id="element-1380" data-mp="3j59tgihdq11v">
              Kennisbank
            </a>
            <span className="text-navy-500" data-id="element-1381" data-mp="3t1uz96183g6r">/</span>
            <span className="text-accent-light font-medium" data-id="element-1382" data-mp="3t13d0ltimut5">
              Kosten website
            </span>
          </nav>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white mb-6 animate-fade-in-up" data-id="element-1383" data-mp="30dqtylmkd54l">
            Wat kost een website laten maken?
          </h1>
          <p className="text-lg lg:text-xl text-navy-200 max-w-2xl mx-auto mb-10 animate-fade-in-up-delay-1" data-id="element-1384" data-mp="1fudluqnb3mpm">
            Een overzicht van de kosten voor een professionele bedrijfswebsite.
            Van starterswebsite tot volledig maatwerk.
          </p>
          <div className="animate-fade-in-up-delay-2" data-id="element-1385" data-mp="1avtz4o0w26w7">
            <a href="#contact" onClick={scrollToContact} className="inline-flex items-center gap-2 bg-accent hover:bg-accent-hover text-white font-bold px-8 py-4 rounded-button shadow-medium hover:shadow-card transition-all duration-200 text-lg" data-id="element-1386" data-mp="1au91gpja27u4">
              Vraag een prijsindicatie aan
              <ArrowRightIcon className="w-5 h-5" data-id="element-1387" data-mp="1tmbkg7je607j" />
            </a>
          </div>
        </div>
      </section>

      {/* Gemiddelde kosten */}
      <section className="py-24 bg-surface-muted" data-id="element-1388" data-mp="e8fiaprbo7xn">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1389" data-mp="i86ej8zmbg9k">
          <div className="text-center mb-16" data-id="element-1390" data-mp="2kuljjr6lmcp0">
            <h2 className="text-3xl lg:text-4xl font-bold text-navy-900 mb-4" data-id="element-1391" data-mp="2kta58xfj1mjn">
              Gemiddelde kosten per type website
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto" data-id="element-1392" data-mp="2fxuvagkn4asi">
              De prijs van een website hangt af van het type en de complexiteit.
              Hieronder een indicatie.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-1393" data-mp="223u0fxwt77xf">
            {/* Starter website */}
            <div className="bg-white rounded-card shadow-card p-8 flex flex-col" data-id="element-1394" data-mp="1x4r5x7eggk3p">
              <h3 className="text-xl font-bold text-navy-900 mb-2" data-id="element-1395" data-mp="1s5ea3x5lw2oz">
                Starter website
              </h3>
              <div className="text-3xl font-extrabold text-navy-900 mb-4" data-id="element-1396" data-mp="1n7wch2x1h9wa">
                Vanaf €500
              </div>
              <p className="text-slate-600 mb-6 flex-grow" data-id="element-1397" data-mp="3mjf9qk8zctq4">
                Een eenvoudige website met de basis. Ideaal voor zzp'ers die
                snel online willen.
              </p>
              <ul className="space-y-3 mb-8" data-id="element-1398" data-mp="3q13aj4bzj0as">
                <li className="flex items-start gap-3" data-id="element-1399" data-mp="3u3gd5jvfqi3c">
                  <CheckIcon className="w-5 h-5 text-accent flex-shrink-0" data-id="element-1400" data-mp="3u1wwvt9s471k" />
                  <span className="text-slate-700 text-sm" data-id="element-1401" data-mp="2ptuw627v2qh">1-3 pagina's</span>
                </li>
                <li className="flex items-start gap-3" data-id="element-1402" data-mp="7mu0ygz2g5hl">
                  <CheckIcon className="w-5 h-5 text-accent flex-shrink-0" data-id="element-1403" data-mp="7pq2efa4hcas" />
                  <span className="text-slate-700 text-sm" data-id="element-1404" data-mp="374tmppba6vdl">
                    Responsive design
                  </span>
                </li>
                <li className="flex items-start gap-3" data-id="element-1405" data-mp="3g35n2j1y97j8">
                  <CheckIcon className="w-5 h-5 text-accent flex-shrink-0" data-id="element-1406" data-mp="3g2dtfdrwxjvl" />
                  <span className="text-slate-700 text-sm" data-id="element-1407" data-mp="3l3kxp2uuk0xi">
                    Contactformulier
                  </span>
                </li>
                <li className="flex items-start gap-3" data-id="element-1408" data-mp="vfu58kfhl80q">
                  <CheckIcon className="w-5 h-5 text-accent flex-shrink-0" data-id="element-1409" data-mp="10b9c2sf2u3nn" />
                  <span className="text-slate-700 text-sm" data-id="element-1410" data-mp="1lftq73yf3hmf">Basis SEO</span>
                </li>
              </ul>
            </div>

            {/* Bedrijfswebsite */}
            <div className="bg-white rounded-card shadow-card p-8 flex flex-col relative border-2 border-accent transform md:-translate-y-2" data-id="element-1411" data-mp="1v6eik9nhzpww">
              <div className="absolute -top-3 right-6 bg-accent text-white text-xs font-bold px-3 py-1 rounded-full" data-id="element-1412" data-mp="1qen1cpq8ewjf">
                Meest gekozen
              </div>
              <h3 className="text-xl font-bold text-navy-900 mb-2" data-id="element-1413" data-mp="205rfjn832dd7">
                Bedrijfswebsite
              </h3>
              <div className="text-3xl font-extrabold text-navy-900 mb-4" data-id="element-1414" data-mp="2e5irlif1hjok">
                Vanaf €1.500
              </div>
              <p className="text-slate-600 mb-6 flex-grow" data-id="element-1415" data-mp="296pc5kg6zilr">
                Een professionele website die jouw bedrijf goed presenteert en
                klanten aantrekt.
              </p>
              <ul className="space-y-3 mb-8" data-id="element-1416" data-mp="etg9ocm2cxd0">
                <li className="flex items-start gap-3" data-id="element-1417" data-mp="2qfi5kmo5dh79">
                  <CheckIcon className="w-5 h-5 text-accent flex-shrink-0" data-id="element-1418" data-mp="2qgtbas15kz10" />
                  <span className="text-slate-700 text-sm" data-id="element-1419" data-mp="2veus16b3wm9b">5-10 pagina's</span>
                </li>
                <li className="flex items-start gap-3" data-id="element-1420" data-mp="2hnzpebr7b1e6">
                  <CheckIcon className="w-5 h-5 text-accent flex-shrink-0" data-id="element-1421" data-mp="2hldhuc4b8lvy" />
                  <span className="text-slate-700 text-sm" data-id="element-1422" data-mp="2lmyt5t174s41">
                    SEO-optimalisatie
                  </span>
                </li>
                <li className="flex items-start gap-3" data-id="element-1423" data-mp="2cntwytd3xav1">
                  <CheckIcon className="w-5 h-5 text-accent flex-shrink-0" data-id="element-1424" data-mp="2cper4hviersd" />
                  <span className="text-slate-700 text-sm" data-id="element-1425" data-mp="1ypnf2mojzlh0">
                    CMS (WordPress)
                  </span>
                </li>
                <li className="flex items-start gap-3" data-id="element-1426" data-mp="22rqshgus904x">
                  <CheckIcon className="w-5 h-5 text-accent flex-shrink-0" data-id="element-1427" data-mp="3t3oeb8o0mahm" />
                  <span className="text-slate-700 text-sm" data-id="element-1428" data-mp="yu9n7umsta9">
                    Google Analytics
                  </span>
                </li>
              </ul>
            </div>

            {/* Maatwerk website */}
            <div className="bg-white rounded-card shadow-card p-8 flex flex-col" data-id="element-1429" data-mp="m3zbk666ulvc">
              <h3 className="text-xl font-bold text-navy-900 mb-2" data-id="element-1430" data-mp="7et4jw13zrcw">
                Maatwerk website
              </h3>
              <div className="text-3xl font-extrabold text-navy-900 mb-4" data-id="element-1431" data-mp="cdwarjsts3mg">
                Vanaf €3.000
              </div>
              <p className="text-slate-600 mb-6 flex-grow" data-id="element-1432" data-mp="103i6tydwi2q8">
                Een volledig op maat gemaakte website met geavanceerde
                functionaliteiten.
              </p>
              <ul className="space-y-3 mb-8" data-id="element-1433" data-mp="r1hcpadyrwss">
                <li className="flex items-start gap-3" data-id="element-1434" data-mp="v3cd8co2m65i">
                  <CheckIcon className="w-5 h-5 text-accent flex-shrink-0" data-id="element-1435" data-mp="v21cymtu3gj5" />
                  <span className="text-slate-700 text-sm" data-id="element-1436" data-mp="3br6otfna94hs">
                    Onbeperkt pagina's
                  </span>
                </li>
                <li className="flex items-start gap-3" data-id="element-1437" data-mp="3fria8ungz8ub">
                  <CheckIcon className="w-5 h-5 text-accent flex-shrink-0" data-id="element-1438" data-mp="389akvvdyj6z4" />
                  <span className="text-slate-700 text-sm" data-id="element-1439" data-mp="33br00cmr7vy7">
                    Koppelingen & integraties
                  </span>
                </li>
                <li className="flex items-start gap-3" data-id="element-1440" data-mp="3i7qdy3wjoqb9">
                  <CheckIcon className="w-5 h-5 text-accent flex-shrink-0" data-id="element-1441" data-mp="3d95foa0usgpg" />
                  <span className="text-slate-700 text-sm" data-id="element-1442" data-mp="2pjktzjdbf5ch">
                    Geavanceerde SEO
                  </span>
                </li>
                <li className="flex items-start gap-3" data-id="element-1443" data-mp="2km2mlxey4y6l">
                  <CheckIcon className="w-5 h-5 text-accent flex-shrink-0" data-id="element-1444" data-mp="2z9dw7trki842" />
                  <span className="text-slate-700 text-sm" data-id="element-1445" data-mp="2zbgvl4em1dlg">
                    Maatwerkfuncties
                  </span>
                </li>
              </ul>
            </div>

            {/* Webshop */}
            <div className="bg-white rounded-card shadow-card p-8 flex flex-col" data-id="element-1446" data-mp="21u9qhu4kywun">
              <h3 className="text-xl font-bold text-navy-900 mb-2" data-id="element-1447" data-mp="21s5ndeeia1vf">Webshop</h3>
              <div className="text-3xl font-extrabold text-navy-900 mb-4" data-id="element-1448" data-mp="2rhvxu2mvf37l">
                Vanaf €2.500
              </div>
              <p className="text-slate-600 mb-6 flex-grow" data-id="element-1449" data-mp="2vhmkpv9a0z6u">
                Een complete webshop met productbeheer, betaalsysteem en
                voorraadbeheer.
              </p>
              <ul className="space-y-3 mb-8" data-id="element-1450" data-mp="3590pahg34p6u">
                <li className="flex items-start gap-3" data-id="element-1451" data-mp="28k1gn1bl9le4">
                  <CheckIcon className="w-5 h-5 text-accent flex-shrink-0" data-id="element-1452" data-mp="28lmccu9n3adk" />
                  <span className="text-slate-700 text-sm" data-id="element-1453" data-mp="2clwkacgzsav2">WooCommerce</span>
                </li>
                <li className="flex items-start gap-3" data-id="element-1454" data-mp="2hjg926bbg619">
                  <CheckIcon className="w-5 h-5 text-accent flex-shrink-0" data-id="element-1455" data-mp="2mj17mfmkqcdg" />
                  <span className="text-slate-700 text-sm" data-id="element-1456" data-mp="2mgy6p0jvuyty">iDEAL / Mollie</span>
                </li>
                <li className="flex items-start gap-3" data-id="element-1457" data-mp="3t1qzom8cb0f0">
                  <CheckIcon className="w-5 h-5 text-accent flex-shrink-0" data-id="element-1458" data-mp="1rsbq9se4bhx" />
                  <span className="text-slate-700 text-sm" data-id="element-1459" data-mp="1mbnrdxjrfuk4">Productbeheer</span>
                </li>
                <li className="flex items-start gap-3" data-id="element-1460" data-mp="1hbhvu6addnwr">
                  <CheckIcon className="w-5 h-5 text-accent flex-shrink-0" data-id="element-1461" data-mp="1he49mnnusznf" />
                  <span className="text-slate-700 text-sm" data-id="element-1462" data-mp="1cjqd2hxggmh6">Verzendopties</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Waaruit bestaat de prijs */}
      <section className="py-24 bg-white" data-id="element-1463" data-mp="13ijqps84khg0">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1464" data-mp="yl0boq3ls0n0">
          <div className="text-center mb-16" data-id="element-1465" data-mp="ynwb6iv3mxdk">
            <h2 className="text-3xl lg:text-4xl font-bold text-navy-900 mb-4" data-id="element-1466" data-mp="tni3j1xetpg2">
              Waaruit bestaat de prijs van een website?
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto" data-id="element-1467" data-mp="opqd6cceybdz">
              Een website laten maken omvat meer dan alleen design. Dit zijn de
              onderdelen.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-1468" data-mp="2r22y0p2b1xy6">
            <div className="bg-surface-muted p-8 rounded-card border border-navy-100" data-id="element-1469" data-mp="2qzzxhb3j0mf7">
              <div className="w-12 h-12 rounded-xl bg-accent-light flex items-center justify-center mb-6" data-id="element-1470" data-mp="2w0e72xks04fc">
                <PaletteIcon className="w-6 h-6 text-accent" data-id="element-1471" data-mp="2h5zlcp9tugwz" />
              </div>
              <h3 className="text-xl font-bold text-navy-900 mb-3" data-id="element-1472" data-mp="2m1f15mreamw1">Design</h3>
              <p className="text-slate-600 leading-relaxed" data-id="element-1473" data-mp="28ajvskg3unx7">
                Het visuele ontwerp van je website, afgestemd op jouw huisstijl
                en doelgroep.
              </p>
            </div>
            <div className="bg-surface-muted p-8 rounded-card border border-navy-100" data-id="element-1474" data-mp="1yeqaocoblm3t">
              <div className="w-12 h-12 rounded-xl bg-accent-light flex items-center justify-center mb-6" data-id="element-1475" data-mp="23djrof2tfv8q">
                <CodeIcon className="w-6 h-6 text-accent" data-id="element-1476" data-mp="1pe0kbohtwnxh" />
              </div>
              <h3 className="text-xl font-bold text-navy-900 mb-3" data-id="element-1477" data-mp="1tekf4lexjfgg">
                Ontwikkeling
              </h3>
              <p className="text-slate-600 leading-relaxed" data-id="element-1478" data-mp="1ar1x65q08fok">
                Het technisch bouwen van de website, inclusief responsiveness en
                snelheid.
              </p>
            </div>
            <div className="bg-surface-muted p-8 rounded-card border border-navy-100" data-id="element-1479" data-mp="1jrhwr43mn2o5">
              <div className="w-12 h-12 rounded-xl bg-accent-light flex items-center justify-center mb-6" data-id="element-1480" data-mp="1eqke6qepg6m4">
                <SearchIcon className="w-6 h-6 text-accent" data-id="element-1481" data-mp="1tnl912s70hot" />
              </div>
              <h3 className="text-xl font-bold text-navy-900 mb-3" data-id="element-1482" data-mp="1oql2kqrflcx6">
                SEO basis
              </h3>
              <p className="text-slate-600 leading-relaxed" data-id="element-1483" data-mp="22g4zhfybzfyk">
                Technische SEO, meta-tags, sitemap en snelheidsoptimalisatie
                voor Google.
              </p>
            </div>
            <div className="bg-surface-muted p-8 rounded-card border border-navy-100" data-id="element-1484" data-mp="2cg5mbbvese7i">
              <div className="w-12 h-12 rounded-xl bg-accent-light flex items-center justify-center mb-6" data-id="element-1485" data-mp="27h2ifur60e14">
                <ServerIcon className="w-6 h-6 text-accent" data-id="element-1486" data-mp="153wjv2r2pj1g" />
              </div>
              <h3 className="text-xl font-bold text-navy-900 mb-3" data-id="element-1487" data-mp="193ostgkdpr7c">
                Hosting en onderhoud
              </h3>
              <p className="text-slate-600 leading-relaxed" data-id="element-1488" data-mp="1e2ro49ak4j24">
                Betrouwbare hosting, updates, backups en technisch beheer na
                oplevering.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Kosten voorbeeld */}
      <section className="py-24 bg-surface-muted" data-id="element-1489" data-mp="1sro5iwyi78fe">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1490" data-mp="1suk14emvpl0o">
          <div className="text-center mb-16" data-id="element-1491" data-mp="1wvcdvhuym81p">
            <h2 className="text-3xl lg:text-4xl font-bold text-navy-900 mb-4" data-id="element-1492" data-mp="21t5hq48sipyt">
              Voorbeelden uit de praktijk
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto" data-id="element-1493" data-mp="2gt6p1zh3gdc">
              Wat kost een website voor jouw type bedrijf? Drie veelvoorkomende
              scenario's.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-1494" data-mp="3w215c0wn4erc">
            <div className="bg-white rounded-card shadow-card p-8" data-id="element-1495" data-mp="3raj4rdyj4g6w">
              <div className="w-12 h-12 rounded-xl bg-accent-light flex items-center justify-center mb-6" data-id="element-1496" data-mp="3r86gyk63bblx">
                <UserIcon className="w-6 h-6 text-accent" data-id="element-1497" data-mp="3m8trrsrqxsg6" />
              </div>
              <h3 className="text-xl font-bold text-navy-900 mb-2" data-id="element-1498" data-mp="3hdwlgtlupx6z">
                Website voor zzp'er
              </h3>
              <div className="text-2xl font-extrabold text-accent mb-6" data-id="element-1499" data-mp="is3fwx9hgl6k">
                €500 – €1.200
              </div>
              <ul className="space-y-3 mb-8" data-id="element-1500" data-mp="8vqq4ah26rh3">
                <li className="flex items-start gap-3" data-id="element-1501" data-mp="8ugoxcewt368">
                  <CheckIcon className="w-5 h-5 text-green-500 flex-shrink-0" data-id="element-1502" data-mp="4v7z2imzppfy" />
                  <span className="text-slate-700 text-sm" data-id="element-1503" data-mp="2tmiqsh089piu">
                    One-pager of 3 pagina's
                  </span>
                </li>
                <li className="flex items-start gap-3" data-id="element-1504" data-mp="2pk47xwwk6prk">
                  <CheckIcon className="w-5 h-5 text-green-500 flex-shrink-0" data-id="element-1505" data-mp="skj9xgknamfq" />
                  <span className="text-slate-700 text-sm" data-id="element-1506" data-mp="smm84nvyblvj">
                    Contactformulier
                  </span>
                </li>
                <li className="flex items-start gap-3" data-id="element-1507" data-mp="jkmtg28estvr">
                  <CheckIcon className="w-5 h-5 text-green-500 flex-shrink-0" data-id="element-1508" data-mp="jlxsxputnfh2" />
                  <span className="text-slate-700 text-sm" data-id="element-1509" data-mp="okscwv4iza2r">Basis SEO</span>
                </li>
                <li className="flex items-start gap-3" data-id="element-1510" data-mp="1c923sbglio0r">
                  <CheckIcon className="w-5 h-5 text-green-500 flex-shrink-0" data-id="element-1511" data-mp="1ga3wsajx63ua" />
                  <span className="text-slate-700 text-sm" data-id="element-1512" data-mp="12ip9vsmw5ihy">
                    Responsive design
                  </span>
                </li>
              </ul>
              <div className="p-4 bg-navy-50 rounded-xl text-sm text-navy-700 italic border-l-4 border-accent" data-id="element-1513" data-mp="17fpl0hyllbfx">
                "Perfect voor freelancers en starters die snel professioneel
                online willen."
              </div>
            </div>

            <div className="bg-white rounded-card shadow-card p-8" data-id="element-1514" data-mp="29bv80dfwtu89">
              <div className="w-12 h-12 rounded-xl bg-accent-light flex items-center justify-center mb-6" data-id="element-1515" data-mp="25b1k3k2gwffd">
                <BuildingIcon className="w-6 h-6 text-accent" data-id="element-1516" data-mp="2j26o0ycvbtvi" />
              </div>
              <h3 className="text-xl font-bold text-navy-900 mb-2" data-id="element-1517" data-mp="2e3d9r3prbyua">
                Website voor mkb
              </h3>
              <div className="text-2xl font-extrabold text-accent mb-6" data-id="element-1518" data-mp="1qd964wckjz3a">
                €1.500 – €3.500
              </div>
              <ul className="space-y-3 mb-8" data-id="element-1519" data-mp="1md8svf17htxt">
                <li className="flex items-start gap-3" data-id="element-1520" data-mp="1mejxti6e17qi">
                  <CheckIcon className="w-5 h-5 text-green-500 flex-shrink-0" data-id="element-1521" data-mp="20crppbokhiut" />
                  <span className="text-slate-700 text-sm" data-id="element-1522" data-mp="1vcm349x5mmjl">5-15 pagina's</span>
                </li>
                <li className="flex items-start gap-3" data-id="element-1523" data-mp="3bqc8rl3x9ivc">
                  <CheckIcon className="w-5 h-5 text-green-500 flex-shrink-0" data-id="element-1524" data-mp="3bsygbkqtbydk" />
                  <span className="text-slate-700 text-sm" data-id="element-1525" data-mp="36rsn7lt8q93i">WordPress CMS</span>
                </li>
                <li className="flex items-start gap-3" data-id="element-1526" data-mp="arms20u3d6p9">
                  <CheckIcon className="w-5 h-5 text-green-500 flex-shrink-0" data-id="element-1527" data-mp="5t32zrk7beso" />
                  <span className="text-slate-700 text-sm" data-id="element-1528" data-mp="jrt19airbkim">
                    SEO-optimalisatie
                  </span>
                </li>
                <li className="flex items-start gap-3" data-id="element-1529" data-mp="fpobimvfj819">
                  <CheckIcon className="w-5 h-5 text-green-500 flex-shrink-0" data-id="element-1530" data-mp="tpz2rr0niomy" />
                  <span className="text-slate-700 text-sm" data-id="element-1531" data-mp="tnw30f9p5h51">
                    Google Analytics & Blog
                  </span>
                </li>
              </ul>
              <div className="p-4 bg-navy-50 rounded-xl text-sm text-navy-700 italic border-l-4 border-accent" data-id="element-1532" data-mp="13fil8vkgcgha">
                "De meest gekozen optie voor groeiende bedrijven in Alkmaar en
                omgeving."
              </div>
            </div>

            <div className="bg-white rounded-card shadow-card p-8" data-id="element-1533" data-mp="17i5hd8o5onka">
              <div className="w-12 h-12 rounded-xl bg-accent-light flex items-center justify-center mb-6" data-id="element-1534" data-mp="17fspo3sliyu1">
                <SettingsIcon className="w-6 h-6 text-accent" data-id="element-1535" data-mp="1r1m47cwge3yo" />
              </div>
              <h3 className="text-xl font-bold text-navy-900 mb-2" data-id="element-1536" data-mp="1w0fllgev2f44">
                Maatwerk website
              </h3>
              <div className="text-2xl font-extrabold text-accent mb-6" data-id="element-1537" data-mp="1m4c85eek6u1v">
                €3.000 – €8.000+
              </div>
              <ul className="space-y-3 mb-8" data-id="element-1538" data-mp="18cnydhrs8ho2">
                <li className="flex items-start gap-3" data-id="element-1539" data-mp="18bmjf27nbb12">
                  <CheckIcon className="w-5 h-5 text-green-500 flex-shrink-0" data-id="element-1540" data-mp="1d3xki8e4h2v7" />
                  <span className="text-slate-700 text-sm" data-id="element-1541" data-mp="yf1e0frprxwp">
                    Onbeperkt pagina's
                  </span>
                </li>
                <li className="flex items-start gap-3" data-id="element-1542" data-mp="13ee1n2qet90c">
                  <CheckIcon className="w-5 h-5 text-green-500 flex-shrink-0" data-id="element-1543" data-mp="pdjs2grml203" />
                  <span className="text-slate-700 text-sm" data-id="element-1544" data-mp="ud6glmcwm4o7">
                    Koppelingen (CRM, boekingen)
                  </span>
                </li>
                <li className="flex items-start gap-3" data-id="element-1545" data-mp="2w33wutraxm0h">
                  <CheckIcon className="w-5 h-5 text-green-500 flex-shrink-0" data-id="element-1546" data-mp="2rbvlhs6evqko" />
                  <span className="text-slate-700 text-sm" data-id="element-1547" data-mp="2r9smiina6n3t">
                    Geavanceerde functies
                  </span>
                </li>
                <li className="flex items-start gap-3" data-id="element-1548" data-mp="2ibyrdb195zi8">
                  <CheckIcon className="w-5 h-5 text-green-500 flex-shrink-0" data-id="element-1549" data-mp="3f17t4nm3bqkv" />
                  <span className="text-slate-700 text-sm" data-id="element-1550" data-mp="3ez3r6b7r51n8">
                    Doorlopend onderhoud
                  </span>
                </li>
              </ul>
              <div className="p-4 bg-navy-50 rounded-xl text-sm text-navy-700 italic border-l-4 border-accent" data-id="element-1551" data-mp="35zgm9fftddpf">
                "Voor bedrijven die specifieke functionaliteiten en integraties
                nodig hebben."
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Wat bepaalt de prijs */}
      <section className="py-24 bg-navy-900 relative overflow-hidden" data-id="element-1552" data-mp="1pocne1o44suu">
        <div className="absolute inset-0 opacity-[0.03]" style={{
        backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)',
        backgroundSize: '40px 40px'
      }} data-id="element-1553" data-mp="55sgyt8yj9r5" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1554" data-mp="ivduvp7wgysb">
          <div className="text-center mb-16" data-id="element-1555" data-mp="ixgtgxn4c08n">
            <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4" data-id="element-1556" data-mp="ntnohvdz7rbt">
              Wat bepaalt de prijs van jouw website?
            </h2>
            <p className="text-lg text-navy-300 max-w-2xl mx-auto" data-id="element-1557" data-mp="svms96dmoq9a">
              Verschillende factoren beïnvloeden de uiteindelijke kosten.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-1558" data-mp="11tygj1r0vbyk">
            <div className="bg-white/[0.06] border border-white/10 rounded-2xl p-6" data-id="element-1559" data-mp="2zlb2bsuy4kh8">
              <h3 className="text-xl font-bold text-white mb-3" data-id="element-1560" data-mp="33l36zuh7y6hb">
                Aantal pagina's
              </h3>
              <p className="text-navy-300 leading-relaxed" data-id="element-1561" data-mp="28adcspcj6g8s">
                Meer pagina's betekent meer content en design. Een one-pager is
                goedkoper dan een website met 20 pagina's.
              </p>
            </div>
            <div className="bg-white/[0.06] border border-white/10 rounded-2xl p-6" data-id="element-1562" data-mp="2i7rrajew8uyg">
              <h3 className="text-xl font-bold text-white mb-3" data-id="element-1563" data-mp="2dbkk6narhdex">
                Functionaliteiten
              </h3>
              <p className="text-navy-300 leading-relaxed" data-id="element-1564" data-mp="1piubcmy5na8v">
                Contactformulieren, boekingssystemen of klantportalen verhogen
                de complexiteit en kosten.
              </p>
            </div>
            <div className="bg-white/[0.06] border border-white/10 rounded-2xl p-6" data-id="element-1565" data-mp="1ukjoq98uo004">
              <h3 className="text-xl font-bold text-white mb-3" data-id="element-1566" data-mp="1uigs36d6z8mf">SEO</h3>
              <p className="text-navy-300 leading-relaxed" data-id="element-1567" data-mp="16surceh68lcb">
                Basis SEO is standaard inbegrepen. Uitgebreide SEO-strategie en
                contentcreatie is een meerwaarde.
              </p>
            </div>
            <div className="bg-white/[0.06] border border-white/10 rounded-2xl p-6" data-id="element-1568" data-mp="3dh4tdoz1v8zq">
              <h3 className="text-xl font-bold text-white mb-3" data-id="element-1569" data-mp="3ibr9g5g2tpq5">Integraties</h3>
              <p className="text-navy-300 leading-relaxed" data-id="element-1570" data-mp="34jul8chj3zz1">
                Koppelingen met externe systemen zoals CRM, boekhoudpakketten of
                betaalsystemen.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Gerelateerde pagina's */}
      <section className="py-16 bg-white border-b border-navy-100" data-id="element-1571" data-mp="51uyb0tkv41g">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-1572" data-mp="3n9amj211fwgi">
          <h3 className="text-lg font-semibold text-navy-900 mb-6" data-id="element-1573" data-mp="3s7u8v3jjna9e">
            Ontdek meer:
          </h3>
          <div className="flex flex-wrap justify-center gap-4" data-id="element-1574" data-mp="2btvpxzcqr6lx">
            <a href="#/website-laten-maken" className="text-accent hover:text-accent-hover font-medium underline underline-offset-4" data-id="element-1575" data-mp="2bsaxcf9zlxqp">
              Website laten maken
            </a>
            <span className="text-navy-200 hidden sm:inline" data-id="element-1576" data-mp="hddqlq4s72ja">•</span>
            <a href="#/website-laten-maken-checklist" className="text-accent hover:text-accent-hover font-medium underline underline-offset-4" data-id="element-1577" data-mp="he5nd4a476b5">
              Checklist voor je nieuwe website
            </a>
            <span className="text-navy-200 hidden sm:inline" data-id="element-1578" data-mp="3mifs8aq2bd2">•</span>
            <a href="#/website-laten-maken-of-zelf-bouwen" className="text-accent hover:text-accent-hover font-medium underline underline-offset-4" data-id="element-1579" data-mp="3jmi8l2sdop5">
              Zelf bouwen of uitbesteden?
            </a>
            <span className="text-navy-200 hidden sm:inline" data-id="element-1580" data-mp="3lsndhbdqzsiy">•</span>
            <a href="#/" className="text-accent hover:text-accent-hover font-medium underline underline-offset-4" data-id="element-1581" data-mp="1o1key03hid2a">
              Terug naar homepage
            </a>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-24 bg-surface-muted" data-id="element-1582" data-mp="31vrgfmwspqvb">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1583" data-mp="36w5c05h7n8cg">
          <div className="text-center mb-16" data-id="element-1584" data-mp="3bnnfb06phl0l">
            <h2 className="text-3xl lg:text-4xl font-bold text-navy-900 mb-4" data-id="element-1585" data-mp="3boq85dnl9xj5">
              Veelgestelde vragen over websitekosten
            </h2>
          </div>

          <div className="space-y-4" data-id="element-1586" data-mp="2iz9gwjbpgmoi">
            {faqs.map((faq, index) => <div key={index} className="bg-white rounded-xl shadow-soft overflow-hidden" data-id="element-1587" data-mp="2j1cg9tyqzs5w" data-mpi={index}>
                <button onClick={() => toggleFaq(index)} className="w-full px-6 py-5 flex items-center justify-between text-left focus:outline-none" data-id="element-1588" data-mp="2rzeksjhox2xx">
                  <span className="font-bold text-navy-900" data-id="element-1589" data-mp="78f9flps8h0p">{faq.q}</span>
                  {openFaq === index ? <ChevronUpIcon className="w-5 h-5 text-accent flex-shrink-0" data-id="element-1590" data-mp="c6yvdm4dlst2" /> : <ChevronDownIcon className="w-5 h-5 text-navy-400 flex-shrink-0" data-id="element-1591" data-mp="3k1vvddchha0q" />}
                </button>
                {openFaq === index && <div className="px-6 pb-5 text-slate-600 leading-relaxed" data-id="element-1592" data-mp="3supmc81alxuf">
                    {faq.a}
                  </div>}
              </div>)}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <FinalCtaSection data-id="element-1593" data-mp="6oakzyxy62cu" />
    </main>;
}